/**
 * Cover Page Widget (Story 5.16, Task 4)
 *
 * Renders a branded cover page for reports. Config-driven with no integration data.
 * Uses CSS transform: scale() to show a scaled-down A4 miniature within 3 grid rows (240px).
 * WYSIWYG exception: 3 canvas rows → 1 full A4 page in PDF.
 *
 * reportingPeriod and clientName are derived at render time (NOT from widget config).
 */

import type { WidgetConfig } from "../../../lib/templateUtils";

interface CoverPageWidgetProps {
  widget: WidgetConfig;
  /** ISO date string for range start, derived from report date selection */
  reportDateRangeStart?: string;
  /** ISO date string for range end, derived from report date selection */
  reportDateRangeEnd?: string;
  /** Client name from selected client (derived, not stored in config) */
  selectedClientName?: string;
  /** Client logo URL from client profile */
  selectedClientLogoUrl?: string;
  /** Report format for badge text and layout (Story 5.23) */
  format?: "document" | "slide-deck";
}

// ── Background Style Classes ──────────────────────────────────────────

const BACKGROUND_CLASSES: Record<string, { bg: string; text: string }> = {
  "gradient-blue": { bg: "bg-gradient-to-br from-blue-600 to-blue-900", text: "text-white" },
  "gradient-dark": { bg: "bg-gradient-to-br from-gray-800 to-gray-950", text: "text-white" },
  "solid-white": { bg: "bg-white border border-border", text: "text-gray-900" },
  "solid-dark": { bg: "bg-gray-900", text: "text-white" },
  "gradient-green": { bg: "bg-gradient-to-br from-emerald-600 to-emerald-900", text: "text-white" },
  "gradient-purple": { bg: "bg-gradient-to-br from-purple-600 to-purple-900", text: "text-white" },
};

// ── Date Formatting ───────────────────────────────────────────────────

function formatReportingPeriod(start?: string, end?: string): string {
  if (!start || !end) return "Select a date range";
  try {
    const fmt = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });
    const s = fmt.format(new Date(start + "T00:00:00"));
    const e = fmt.format(new Date(end + "T00:00:00"));
    return `${s} – ${e}`;
  } catch {
    return "Invalid date range";
  }
}

// ── Component ─────────────────────────────────────────────────────────

export function CoverPageWidget({
  widget,
  reportDateRangeStart,
  reportDateRangeEnd,
  selectedClientName,
  selectedClientLogoUrl,
  format = "document",
}: CoverPageWidgetProps) {
  const config = widget.config as {
    title?: string;
    subtitle?: string;
    clientLogoUrl?: string | null;
    agencyLogoUrl?: string | null;
    preparedBy?: string;
    backgroundStyle?: string;
  };

  const title = config.title || "Report Title";
  const subtitle = config.subtitle || "";
  const preparedBy = config.preparedBy || "";
  const backgroundStyle = config.backgroundStyle || "gradient-blue";

  // Logo URLs: config override takes precedence, then auto-pulled from client/tenant
  const clientLogoUrl = config.clientLogoUrl || selectedClientLogoUrl || "";
  const agencyLogoUrl = config.agencyLogoUrl || "";

  // Derived at render time
  const reportingPeriod = formatReportingPeriod(reportDateRangeStart, reportDateRangeEnd);
  const clientName = selectedClientName || "";

  const bgStyle = BACKGROUND_CLASSES[backgroundStyle] ?? BACKGROUND_CLASSES["gradient-blue"]!;

  return (
    <div
      data-testid="widget-type-cover-page"
      className="h-full w-full relative overflow-hidden rounded-lg"
    >
      {/* Scaled A4 miniature preview */}
      <div
        className={`absolute inset-0 flex flex-col items-center justify-center p-6 ${bgStyle.bg} ${bgStyle.text}`}
      >
        <div
          className="flex w-full flex-col items-center justify-center text-center"
          style={{ transform: "scale(0.85)", transformOrigin: "center center" }}
        >
          {/* Agency logo */}
          {agencyLogoUrl && (
            <img
              src={agencyLogoUrl}
              alt="Agency logo"
              data-testid="cover-page-agency-logo"
              className="mb-4 max-h-10 max-w-[180px] object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
          )}

          {/* Report title */}
          <h1
            className="line-clamp-2 max-w-full text-2xl font-bold leading-tight tracking-tight"
            data-testid="cover-page-title"
          >
            {title}
          </h1>

          {/* Subtitle */}
          {subtitle && (
            <p
              className="mt-1.5 line-clamp-1 max-w-full text-sm opacity-80"
              data-testid="cover-page-subtitle"
            >
              {subtitle}
            </p>
          )}

          {/* Metadata block: period · client · prepared-by */}
          <div className="mt-4 flex flex-col items-center gap-1.5">
            {/* Reporting period */}
            <p className="text-xs opacity-70" data-testid="cover-page-period">
              {reportingPeriod}
            </p>

            {/* Client name + logo */}
            {clientName && (
              <p className="text-xs font-medium opacity-90" data-testid="cover-page-client-name">
                {clientName}
              </p>
            )}
            {clientLogoUrl && (
              <img
                src={clientLogoUrl}
                alt="Client logo"
                data-testid="cover-page-client-logo"
                className="max-h-8 max-w-[140px] object-contain"
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
              />
            )}

            {/* Prepared by */}
            {preparedBy && (
              <p className="mt-1 text-xs opacity-60" data-testid="cover-page-prepared-by">
                Prepared by {preparedBy}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* "Full page/slide in PDF" badge */}
      <div className="absolute right-2 top-2 rounded bg-black/40 px-1.5 py-0.5 text-[10px] text-white">
        {format === "slide-deck" ? "Full slide in PDF" : "Full page in PDF"}
      </div>
    </div>
  );
}
